/**
 * The cutting beam (M2-10, spec §6.3): anchor cutting, heat spill into the part, anchor assist and the coolant
 * hazard. Pure and allocation-free (D-014): it reads and writes the M2-17 part pool columns in place and reports
 * through a caller-owned `CutResult`. Positions are float64 world metres (D-011).
 *
 * Per tick with the beam on (M2-17's tools stage): optionally `assistPick` to snap the beam onto an anchor, then
 * `stepCut`. `stepCut` never sets `freed`: when `partFreed` comes back true the runtime frees the part (it builds the
 * physics body) in the same tick.
 */
import { MAX_ANCHORS_PER_PART, type SalvageConfig } from '../data/m2Schemas'
import type { PartPool } from '../space/contracts'

/** The part-pool columns the cutting beam reads and writes. */
export type CutPool = Pick<
  PartPool,
  'high' | 'alive' | 'wreck' | 'freed' | 'secured' | 'anchorCount' | 'ax' | 'ay' | 'az' | 'cut' | 'cutSeconds' | 'coolant' | 'condition'
>

export type BeamConfig = SalvageConfig['beam']
export type CoolantConfig = SalvageConfig['coolant']

/** What the beam did this tick (caller-owned, overwritten by every `stepCut`). */
export interface CutResult {
  /** The beam touches a part (on an anchor or spilling). */
  contact: boolean
  /** Part slot touched, or -1. */
  part: number
  /** Anchor index on `part` (0..MAX_ANCHORS_PER_PART-1), or -1. */
  anchor: number
  /** The beam is on an uncut anchor and cutting it. */
  onAnchor: boolean
  /** The beam is near an anchor but off it (or on a finished one): the part loses condition. */
  spilling: boolean
  /** Cut progress of `anchor` after this tick (0..1). */
  progress: number
  /** `anchor` was finished this tick. */
  anchorCut: boolean
  /** The finished anchor was the coolant anchor: the runtime vents (`coolantVentDamage`). */
  coolant: boolean
  /** The finished anchor was the part's last: the part is free. */
  partFreed: boolean
  /** Contact point on the beam, float64 world (the beam's visible end). */
  hitX: number
  hitY: number
  hitZ: number
}

export function createCutResult(): CutResult {
  return {
    contact: false,
    part: -1,
    anchor: -1,
    onAnchor: false,
    spilling: false,
    progress: 0,
    anchorCut: false,
    coolant: false,
    partFreed: false,
    hitX: 0,
    hitY: 0,
    hitZ: 0,
  }
}

/** An anchor the assist picked (caller-owned). */
export interface AnchorPick {
  part: number
  anchor: number
  /** Anchor world position (float64). */
  x: number
  y: number
  z: number
}

export function createAnchorPick(): AnchorPick {
  return { part: -1, anchor: -1, x: 0, y: 0, z: 0 }
}

/** Every anchor of part `p` is cut. */
export function partFullyCut(pool: Pick<PartPool, 'anchorCount' | 'cut'>, p: number): boolean {
  const base = p * MAX_ANCHORS_PER_PART
  const n = pool.anchorCount[p]
  for (let a = 0; a < n; a++) if (pool.cut[base + a] < 1) return false
  return true
}

/**
 * One tick of the cutting beam from `o` along the unit direction `d`, against the attached parts of wreck `wreck`
 * (-1 = every wreck). A beam passing within `anchorRadius` of an uncut anchor (inside `range`) cuts the closest such
 * anchor: progress += dt / (cutSeconds × cutMult). Otherwise a beam within `spillRadius` of any anchor (cut or not)
 * spills heat into that anchor's part: condition −= spillPerSec × dt. Returns `out.contact`.
 */
export function stepCut(
  pool: CutPool,
  wreck: number,
  ox: number,
  oy: number,
  oz: number,
  dx: number,
  dy: number,
  dz: number,
  dt: number,
  cutMult: number,
  spillPerSec: number,
  cfg: BeamConfig,
  out: CutResult,
): boolean {
  out.contact = false
  out.part = -1
  out.anchor = -1
  out.onAnchor = false
  out.spilling = false
  out.progress = 0
  out.anchorCut = false
  out.coolant = false
  out.partFreed = false

  const maxT = cfg.range
  let onBest = -1
  let onD2 = cfg.anchorRadius * cfg.anchorRadius
  let onT = 0
  let nearBest = -1
  let nearD2 = cfg.spillRadius * cfg.spillRadius
  let nearT = 0
  for (let p = 0; p < pool.high; p++) {
    if (pool.alive[p] === 0 || pool.freed[p] !== 0 || pool.secured[p] !== 0) continue
    if (wreck >= 0 && pool.wreck[p] !== wreck) continue
    const base = p * MAX_ANCHORS_PER_PART
    const n = pool.anchorCount[p]
    for (let a = 0; a < n; a++) {
      const i = base + a
      const rx = pool.ax[i] - ox
      const ry = pool.ay[i] - oy
      const rz = pool.az[i] - oz
      const t = rx * dx + ry * dy + rz * dz
      if (t < 0 || t > maxT) continue
      const d2 = rx * rx + ry * ry + rz * rz - t * t
      if (pool.cut[i] < 1 && d2 <= onD2) {
        onBest = i
        onD2 = d2
        onT = t
      }
      if (d2 <= nearD2) {
        nearBest = i
        nearD2 = d2
        nearT = t
      }
    }
  }

  let t: number
  if (onBest >= 0) {
    const p = Math.floor(onBest / MAX_ANCHORS_PER_PART)
    const time = pool.cutSeconds[onBest] * cutMult
    let progress = pool.cut[onBest] + (time > 0 ? dt / time : 1)
    if (progress >= 1) {
      progress = 1
      out.anchorCut = true
      out.coolant = pool.coolant[onBest] !== 0
    }
    pool.cut[onBest] = progress
    out.part = p
    out.anchor = onBest - p * MAX_ANCHORS_PER_PART
    out.onAnchor = true
    out.progress = progress
    out.partFreed = out.anchorCut && partFullyCut(pool, p)
    t = onT
  } else if (nearBest >= 0) {
    const p = Math.floor(nearBest / MAX_ANCHORS_PER_PART)
    const c = pool.condition[p] - spillPerSec * dt
    pool.condition[p] = c > 0 ? c : 0
    out.part = p
    out.anchor = nearBest - p * MAX_ANCHORS_PER_PART
    out.spilling = true
    out.progress = pool.cut[nearBest]
    t = nearT
  } else {
    t = maxT
  }
  out.contact = out.part >= 0
  out.hitX = ox + dx * t
  out.hitY = oy + dy * t
  out.hitZ = oz + dz * t
  return out.contact
}

/**
 * Anchor assist: the uncut anchor (of an attached part of `wreck`, -1 = any) nearest the reticle direction `d`
 * within the half-angle `assist` (radians) and `range` metres. Ties go to the nearer anchor. The runtime aims the
 * beam straight at the pick. Returns whether one was found.
 */
export function assistPick(
  pool: CutPool,
  wreck: number,
  ox: number,
  oy: number,
  oz: number,
  dx: number,
  dy: number,
  dz: number,
  range: number,
  assist: number,
  out: AnchorPick,
): boolean {
  const minCos = Math.cos(assist)
  const range2 = range * range
  let best = -1
  let bestCos = -1
  let bestDist2 = Infinity
  for (let p = 0; p < pool.high; p++) {
    if (pool.alive[p] === 0 || pool.freed[p] !== 0 || pool.secured[p] !== 0) continue
    if (wreck >= 0 && pool.wreck[p] !== wreck) continue
    const base = p * MAX_ANCHORS_PER_PART
    const n = pool.anchorCount[p]
    for (let a = 0; a < n; a++) {
      const i = base + a
      if (pool.cut[i] >= 1) continue
      const rx = pool.ax[i] - ox
      const ry = pool.ay[i] - oy
      const rz = pool.az[i] - oz
      const dist2 = rx * rx + ry * ry + rz * rz
      if (dist2 > range2 || dist2 === 0) continue
      const cos = (rx * dx + ry * dy + rz * dz) / Math.sqrt(dist2)
      if (cos < minCos) continue
      if (best < 0 || cos > bestCos || (cos === bestCos && dist2 < bestDist2)) {
        best = i
        bestCos = cos
        bestDist2 = dist2
      }
    }
  }
  if (best < 0) {
    out.part = -1
    out.anchor = -1
    return false
  }
  const p = Math.floor(best / MAX_ANCHORS_PER_PART)
  out.part = p
  out.anchor = best - p * MAX_ANCHORS_PER_PART
  out.x = pool.ax[best]
  out.y = pool.ay[best]
  out.z = pool.az[best]
  return true
}

/**
 * Damage a coolant vent at `v` deals to a ship at `s` (spec §6.3): the full `coolant.damage` within `coolant.radius`
 * metres, else 0. Cutting the coolant anchor last from range avoids it.
 */
export function coolantVentDamage(vx: number, vy: number, vz: number, sx: number, sy: number, sz: number, cfg: CoolantConfig): number {
  const dx = sx - vx
  const dy = sy - vy
  const dz = sz - vz
  return dx * dx + dy * dy + dz * dz <= cfg.radius * cfg.radius ? cfg.damage : 0
}
